import {
	AreaChartOutlined,
	EnvironmentOutlined,
	FileImageOutlined,
	FormOutlined,
} from '@ant-design/icons';
import { Menu, MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import {
	CREATE_CENTER,
	CREATE_EXAM,
	CREATE_KANHA_BATCH,
	EDIT_KANHA_BATCH,
	EXAM_MODULE_CENTERS_LIST,
	EXAM_MODULE_ELIGIBLE_CANDIDATES,
	EXAM_MODULE_EXAMS_LIST,
	EXAM_MODULE_HOME,
	EXAM_MODULE_KANHA_ELIGIBLE_CANDIDATES,
	EXAM_MODULE_KANHA_INVITE,
	EXAM_MODULE_VIEW_CANDIDATES,
	EXAM_MODULE_VIEW_HELD_CANDIDATES,
	SEND_ADMIT_CARD_EMAIL,
	SEND_INVITE_EMAIL,
	SEND_RESULTS_EMAIL,
	UPDATE_CENTER,
	UPDATE_EXAM,
	VIEW_CENTER,
	VIEW_EXAM,
} from '../../shared/Routes';
import { useExamModuleIds } from '../../hooks/useExamModuleIds';
import { hasPrivilege } from '../../shared/Utils';
import { PrivilegeCodes } from '../../shared/RoleConstants';
import useUserSession from '../../hooks/useUserSession';
import { useEffect } from 'react';

type MenuItem = Required<MenuProps>['items'][number];

const SideMenu = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const { navigateWithoutExamModuleIds } = useExamModuleIds();
	const { loggedInUserPrivileges } = useUserSession();
	const normalizedPath = location.pathname.replace(/\/$/, '');

	const showRegistrationGraph = hasPrivilege(
		PrivilegeCodes.GET_EXAMINATION_GRAPHICAL_VIEW,
		loggedInUserPrivileges
	);

	const showResultsGraph = hasPrivilege(
		PrivilegeCodes.GET_EXAM_RESULT_GRAPHICAL_VIEW,
		loggedInUserPrivileges
	);

	const showCreateExam = hasPrivilege(
		PrivilegeCodes.CREATE_EXAM,
		loggedInUserPrivileges
	);

	const showUpdateExam = hasPrivilege(
		PrivilegeCodes.UPDATE_EXAM,
		loggedInUserPrivileges
	);

	const showUploadEligibileCandidates = hasPrivilege(
		PrivilegeCodes.IMPORT_ELIGIBLE_CANDIDATES_FOR_EXAM,
		loggedInUserPrivileges
	);

	const showInviteEmail = hasPrivilege(
		PrivilegeCodes.SEND_EXAM_REGISTRATION_INVITE_EMAIL,
		loggedInUserPrivileges
	);

	const showAdmitCardEmail = hasPrivilege(
		PrivilegeCodes.SEND_EXAM_ADMIT_CARD_EMAIL,
		loggedInUserPrivileges
	);

	const showResultEmail = hasPrivilege(
		PrivilegeCodes.SEND_EXAM_RESULT_EMAIL,
		loggedInUserPrivileges
	);

	const showRegistrationDataExport = hasPrivilege(
		PrivilegeCodes.DOWNLOAD_CANDIDATE_EXAMINATION_DETAILS,
		loggedInUserPrivileges
	);

	const showUploadResult = hasPrivilege(
		PrivilegeCodes.UPLOAD_EXAM_RESULTS,
		loggedInUserPrivileges
	);

	const showRegistrationDataHold = hasPrivilege(
		PrivilegeCodes.PLACE_CANDIDATE_ON_HOLD,
		loggedInUserPrivileges
	);

	const showRegistrationDataEdit = hasPrivilege(
		PrivilegeCodes.CHANGE_EXAMINATION_CENTER,
		loggedInUserPrivileges
	);

	const showApproveHold = hasPrivilege(
		PrivilegeCodes.APPROVE_HELD_CANDIDATE,
		loggedInUserPrivileges
	);

	const showCreateExamCenter = hasPrivilege(
		PrivilegeCodes.CREATE_EXAMINATION_CENTER,
		loggedInUserPrivileges
	);

	const showEditExamCenter = hasPrivilege(
		PrivilegeCodes.UPDATE_EXAMINATION_CENTER,
		loggedInUserPrivileges
	);

	const showCreateKanhaBatch = hasPrivilege(
		PrivilegeCodes.CREATE_KANHA_BATCH,
		loggedInUserPrivileges
	);

	const showUpdateKanhaBatch = hasPrivilege(
		PrivilegeCodes.UPDATE_KANHA_BATCH,
		loggedInUserPrivileges
	);

	const showUploadKanhaBatchCandidates = hasPrivilege(
		PrivilegeCodes.IMPORT_ELIGIBLE_CANDIDATES_FOR_KANHA_BATCH,
		loggedInUserPrivileges
	);

	const showKanhaInviteEmail = hasPrivilege(
		PrivilegeCodes.SEND_KANHA_BATCH_INVITE_EMAIL,
		loggedInUserPrivileges
	);

	const showDownloadKanhaDetails = hasPrivilege(
		PrivilegeCodes.DOWNLOAD_KANHA_BATCH_CANDIDATE_DETAILS,
		loggedInUserPrivileges
	);

	const showUploadKanhaResults = hasPrivilege(
		PrivilegeCodes.UPLOAD_KANHA_BATCH_RESULTS,
		loggedInUserPrivileges
	);

	const showExamMenu =
		showCreateExam ||
		showUpdateExam ||
		showUploadEligibileCandidates ||
		showInviteEmail ||
		showAdmitCardEmail ||
		showResultEmail ||
		showRegistrationDataExport ||
		showUploadResult ||
		showRegistrationDataHold ||
		showRegistrationDataEdit ||
		showApproveHold;

	const showKanhaMenu =
		showCreateKanhaBatch ||
		showUpdateKanhaBatch ||
		showUploadKanhaBatchCandidates ||
		showKanhaInviteEmail ||
		showDownloadKanhaDetails ||
		showUploadKanhaResults;

	const getActiveMenuKey = (path: string): string => {
		const examPaths = [
			EXAM_MODULE_EXAMS_LIST,
			CREATE_EXAM,
			UPDATE_EXAM,
			VIEW_EXAM,
			EXAM_MODULE_VIEW_CANDIDATES,
			EXAM_MODULE_VIEW_HELD_CANDIDATES,
			EXAM_MODULE_ELIGIBLE_CANDIDATES,
			SEND_INVITE_EMAIL,
			SEND_ADMIT_CARD_EMAIL,
			SEND_RESULTS_EMAIL,
		];
		const centerPaths = [
			EXAM_MODULE_CENTERS_LIST,
			CREATE_CENTER,
			UPDATE_CENTER,
			VIEW_CENTER,
		];
		const kanhaPaths = [
			EXAM_MODULE_KANHA_INVITE,
			CREATE_KANHA_BATCH,
			EDIT_KANHA_BATCH,
			EXAM_MODULE_KANHA_ELIGIBLE_CANDIDATES,
		];

		if (examPaths.some((p) => path.startsWith(p))) {
			return EXAM_MODULE_EXAMS_LIST;
		}

		if (centerPaths.some((p) => path.startsWith(p))) {
			return EXAM_MODULE_CENTERS_LIST;
		}

		if (kanhaPaths.some((p) => path.startsWith(p))) {
			return EXAM_MODULE_KANHA_INVITE;
		}

		return path;
	};

	const items: MenuItem[] = [];

	if (showRegistrationGraph || showResultsGraph) {
		items.push({
			key: EXAM_MODULE_HOME,
			icon: <AreaChartOutlined style={{ fontSize: 20 }} />,
			label: <span className="font-semibold text-base">{t('dashboard')}</span>,
		});
	}

	if (showExamMenu) {
		items.push({
			key: EXAM_MODULE_EXAMS_LIST,
			icon: <FormOutlined style={{ fontSize: 20 }} />,
			label: <span className="font-semibold text-base">{t('exams')}</span>,
		});
	}

	if (showCreateExamCenter || showEditExamCenter) {
		items.push({
			key: EXAM_MODULE_CENTERS_LIST,
			icon: <EnvironmentOutlined style={{ fontSize: 20 }} />,
			label: <span className="font-semibold text-base">{t('centers')}</span>,
		});
	}

	if (showKanhaMenu) {
		items.push({
			key: EXAM_MODULE_KANHA_INVITE,
			icon: <FileImageOutlined style={{ fontSize: 20 }} />,
			label: (
				<span className="font-semibold text-base">{t('kanhaInvite')}</span>
			),
		});
	}

	const firstAvailableRoute = items.length > 0 ? String(items[0]?.key) : null;

	useEffect(() => {
		if (
			normalizedPath === EXAM_MODULE_HOME &&
			firstAvailableRoute &&
			firstAvailableRoute !== EXAM_MODULE_HOME
		) {
			navigateWithoutExamModuleIds(firstAvailableRoute);
		}
	}, [normalizedPath, firstAvailableRoute, navigateWithoutExamModuleIds]);

	return (
		<Menu
			onClick={({ key }) => {
				navigateWithoutExamModuleIds(key);
			}}
			items={items}
			selectedKeys={[getActiveMenuKey(normalizedPath)]}
		/>
	);
};

const MenuContent = () => {
	return (
		<div className="mx-8 my-2 flex-1 overflow-auto">
			<Outlet />
		</div>
	);
};

export const ExamModuleHome = () => {
	return (
		<div className="flex flex-row min-h-[90vh]">
			<div className="w-48">
				<SideMenu />
			</div>
			<MenuContent />
		</div>
	);
};
